import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { NGXLogger as LoggerService } from "ngx-logger";
import { TranslationGeneralService } from '../shared/translation/translation.general.service';
import { UIMesageService } from '../shared/services/ui-message.service';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { AccessGrantService } from '../whoo/core/access-grant.service';

@Injectable({
    providedIn: 'root',
})
/**
 * Returns false if the user is not logged or if the user has
 *  no edit rights on ownerId that is provided as param from the ActivatedRouteSnapshot
 * otherwise redirect to login ot no-access page
 */
export class AuthGuard {
    constructor(
        private authService: AuthService,
        private uiMessage: UIMesageService,
        private logger: LoggerService,
        private router: Router,
        private userService: UserService,
        private trans: TranslationGeneralService,
        private accessGrantService: AccessGrantService,
    ) { }

    async canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean | UrlTree> {
        this.logger.log('canActivate next.data.id ', next.data.id)
        const isLoggedIn = await this.authService.getIsLoggedIn();
        if (!isLoggedIn) {
            this.logger.log('!isLoggedIn');
            return this.handleNotLoggedIn(next, state);
        }
        return this.handleLoggedIn(next, state);
    }

    private handleNotLoggedIn(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): UrlTree {
        if (next.data.id === 'votePreview') {
            return this.handleVotePreview(next, state)
        } else {
            this.logger.log('access denied; url: ', state.url);
            this.authService.navigateToUrl = state.url;
            // Redirect to the login page
            return this.router.parseUrl('user/login');
        }
    }

    private async handleLoggedIn(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<true | UrlTree> {
        this.logger.log('handleLoggedIn; next: ', next);
        const ownerId = next.params.ownerId;
        if (ownerId) {
            this.logger.log('Owner ID required ', ownerId);
            await this.userService.loadDbUser(this.authService.uid); // ?? this is not a beautifull solutiion but we have to make sure the user is loaded at this point
            // a route on one poll: grant aware (owner, legacy, co-owner, editor - workorder 1.2 task 4b);
            // routes without pollId stay on the token (owner, legacy)
            const pollId = next.params.pollId;
            const allowed = pollId
                ? await this.accessGrantService.canEditPollAsync(ownerId, pollId)
                : this.authService.hasEditRights(this.authService.uid, ownerId);
            if (allowed) {
                this.logger.log('hasEditRights ', ownerId, pollId ?? '');
                return true;
            } else {
                this.uiMessage.warning(this.trans.translate('noAccess'));
                if (next.data.id === 'votePreview') {
                    return this.handleVotePreview(next, state)
                } else {
                    return this.router.parseUrl('user/no-access');
                }
            }
        }
        return true;
    }

    /**
    *   In case a poll ownwer sends by mistake the votePreview link we want
    *   the voter to be redirectd to the vote page.
    */
    private handleVotePreview(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): UrlTree {
        this.logger.warn('No access to "Vote Preview". Redirecting to "Vote"')
        return this.router.parseUrl('vote/' + next.params.ownerId + "/" + next.params.pollId);
    }
}
